/**
 * 온앤미 디자인 토큰
 *
 * 역할:
 * - 이 파일은 "순수 디자인 값"(토큰)과 공개 API(ds)만 관리한다.
 * - 색상, 글자 크기, 글자 굵기처럼 화면 전반에서 재사용되는 값을 정의한다.
 * - 버튼, 카드, 탭, 테이블 같은 컴포넌트별 스타일은 ./presets/ 아래 컴포넌트별 파일에 있다.
 * - MUI 전역 기본값(palette·styleOverrides)은 ./muiTheme.js 에 있다.
 *
 * 구조 (3층):
 *   designSystem.js  토큰 + ds 공개 API   ← 화면에서 import 하는 유일한 진입점
 *   presets/*.js     컴포넌트별 프리셋     ← "표 스타일 어디서 고치지?" → presets/table.js
 *   muiTheme.js      MUI 전역 테마
 *
 * 수정 기준:
 * - 전체 브랜드 색상을 바꾸고 싶으면 colors.primary 계열을 수정한다.
 * - 화면 배경/카드 배경을 바꾸고 싶으면 colors.bg, colors.surface를 수정한다.
 * - 제목/본문 크기를 바꾸고 싶으면 typography.* 값을 수정한다.
 *
 * MUI spacing 단위:
 * - sx의 p, px, py, m, mt 같은 숫자는 기본적으로 theme.spacing(n)이다.
 * - 기본 theme.spacing(1)은 8px이므로 p: 2는 16px, px: 1.5는 12px이다.
 */

import { createComponentStyles } from "./presets";
import { createMuiTheme } from "./muiTheme";

const tokens = {
    /**
     * 색상 토큰
     *
     * primary 계열:
     * - 주요 액션 버튼, 선택 상태, 포커스, 활성 탭에 사용한다.
     * - primary를 바꾸면 앱의 핵심 포인트 색상이 바뀐다.
     * - primaryDark는 hover/pressed 같은 더 강한 상태에 사용한다.
     * - primaryBg/primaryLight는 선택 배경, 연한 칩 배경에 사용한다.
     * - primaryBorder는 선택 칩/카드 hover 테두리에 사용한다.
     *
     * surface/bg/border 계열:
     * - bg는 페이지 전체 배경이다.
     * - surface는 Paper/Card/Dialog 같은 떠 있는 면의 배경이다.
     * - border는 일반 구분선, borderLight는 테이블 행처럼 약한 구분선에 사용한다.
     *
     * text 계열:
     * - textPrimary는 제목/핵심 정보, textSecondary는 설명, textMuted는 보조 정보에 사용한다.
     */
    colors: {
        primary: "#2563eb",
        primaryDark: "#1d4ed8",
        primaryLight: "#dbeafe",
        primaryBg: "#eff6ff",
        primaryBorder: "#93c5fd",
        primarySoft: "#60a5fa",
        primaryText: "#1e40af",
        secondary: "#64748b",
        bg: "#f8fafc",
        surface: "#ffffff",
        rowHover: "#fafbfc",       // 표 본문 행 hover 배경 (bg보다 살짝 더 밝은 톤)
        border: "#e2e8f0",
        borderLight: "#f1f5f9",
        textPrimary: "#0f172a",
        textSecondary: "#475569",
        textMuted: "#94a3b8",
        tooltip: "#1e293b",        // 다크 툴팁 배경/화살표
        success: "#16a34a",
        successBg: "#f0fdf4",
        error: "#dc2626",
        errorBg: "#fef2f2",
        warning: "#d97706",
        warningBg: "#fffbeb",
        info: "#2563eb",
        infoBg: "#eff6ff",
    },

    /**
     * 타이포그래피 토큰
     *
     * fontSize:
     * - px 단위와 동일하게 동작한다. fontSize: 20은 20px이다.
     *
     * fontWeight:
     * - 400: 일반, 500: 약간 강조, 600: 라벨/버튼, 700: 섹션 제목, 800: 페이지 제목 수준이다.
     * - 900 이상은 너무 강해서 화면마다 튀기 쉬우므로 디자인 시스템에서는 기본적으로 피한다.
     *
     * letterSpacing:
     * - 음수 값은 한글/숫자 제목을 조금 더 단단하게 보이게 한다.
     * - 값을 더 낮추면 글자가 붙어 보이고, 0에 가까우면 기본 간격이 된다.
     */
    typography: {
        // 페이지 최상단 제목. HR/결재/검색/제안서 같은 페이지명에 사용한다.
        pageTitle: { fontSize: 20, fontWeight: 800, letterSpacing: "-0.3px", color: "#0f172a" },

        // 페이지 제목 아래 설명. 너무 강하지 않게 보조 정보로 사용한다.
        pageSubtitle: { fontSize: 13, fontWeight: 500, color: "#64748b" },

        // 카드 내부 제목, 모달 제목, 테이블 섹션 제목에 사용한다.
        sectionTitle: { fontSize: 15, fontWeight: 700, color: "#1e293b" },

        // 입력 필드 위 라벨, 필터 라벨처럼 작지만 읽혀야 하는 텍스트에 사용한다.
        label: { fontSize: 12, fontWeight: 600, color: "#64748b" },

        // 일반 본문 텍스트에 사용한다.
        body: { fontSize: 14, color: "#334155" },

        // 보조 설명, 빈 상태 안내, 작은 카운트 등에 사용한다.
        caption: { fontSize: 11, color: "#94a3b8" },
    },

    /**
     * 재무 개념별 표준 색상 (전 시스템 단일 소스).
     * 차트 시리즈·KPI 카드·라벨 등 재무 화면 어디서든 이 값을 쓴다 — 색이 뒤죽박죽이지 않게.
     * 바꾸려면 여기 한 곳만 수정한다. (ds.finance.income 등으로 사용)
     */
    finance: {
        income:           "#16a34a",   // 입금        — 초록
        expense:          "#dc2626",   // 출금        — 빨강
        cumIncome:        "#0891b2",   // 누적 입금   — 청록(임의)
        cumExpense:       "#9f1239",   // 누적 출금   — 진자주(임의)
        balance:          "#0f172a",   // 잔액        — 검정
        scheduledPayment: "#d97706",   // 지급예정액  — 주황
        scheduledExpense: "#7c3aed",   // 지출예정액  — 보라
        baseline:         "#cbd5e1",   // 0 기준선    — 옅은 회색
    },
};

const componentStyles = createComponentStyles(tokens);

export const ds = {
    ...tokens,

    // ./presets/ 의 컴포넌트별 sx preset 모음이다.
    // 신규 코드는 가능하면 ds.components.card, ds.components.external.card처럼 사용한다.
    components: componentStyles,

    /**
     * 기존 사용처 호환 alias — 실제 사용 중인 것만 유지한다.
     * (buttonPrimary/buttonSecondary/tabs/inputSx 는 사용처 0건이라 제거됨.
     *  버튼 기본 스타일은 muiTheme 의 MuiButton styleOverrides 가 담당한다.)
     */
    card: componentStyles.card,
    cardPadded: componentStyles.cardPadded,
    tableSx: componentStyles.table,
    tableMiniSx: componentStyles.tableMini,

    /**
     * 스크롤은 되지만 스크롤바는 보이지 않게 — 표 컨테이너에 얹어 쓴다.
     *
     * <p>왜 필요한가: 표는 컨테이너 폭 100% 를 채우도록 잡혀 있는데, 세로 스크롤바가 뜨면 그 폭(≈15px)만큼
     * 안쪽이 좁아져 <b>표 오른쪽에 회색 띠가 생기고 마지막 열이 잘린다.</b> 스크롤바가 나타났다 사라질 때마다
     * 열 폭이 흔들리는 것도 같은 원인이다. 막대만 숨기면 표는 항상 같은 폭을 쓰고, 휠·트랙패드·키보드·드래그
     * 스크롤은 전부 그대로 동작한다(overflow 값을 바꾸는 게 아니라 막대만 감춘다).
     *
     * <p>접근성: 스크롤 자체를 없애는 게 아니므로 키보드(PageUp/Down·화살표)와 보조기술 탐색에는 영향이 없다.
     */
    hiddenScrollbar: {
        scrollbarWidth: "none",          // Firefox
        msOverflowStyle: "none",         // 구 Edge/IE
        "&::-webkit-scrollbar": { width: 0, height: 0 },   // Chrome·Safari
    },
};

export const muiTheme = createMuiTheme(tokens);
